import { useContext, useState, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { SVG_edit } from '../elements/SVG';
import { getText } from '@educaplay/app-core/language';
import { MainContext } from '../../contexts/main';
import { BillingFormContext } from '../../contexts/billingForm';
import { BlockLoading } from './BlockLoading';
import { BlockBillingAddress } from './BlockBillingAddress';
import { BlockInvoiceByEmail } from './BlockInvoiceByEmail';
import SelectForm from '../inputs/SelectForm';
import InputForm from '../inputs/InputForm';
import { isInvitedWithoutSubscription } from '../../helpers/billing';

export const BlockSummaryBilling = ({ mode }) => {
    const { mainReducer } = useContext(MainContext);
    const billingForm = useContext(BillingFormContext);

    const [stateExpanded, setStateExpanded] = useState(false);
    const [stateLoading, setStateLoading] = useState(true);

    const isDisabled = isInvitedWithoutSubscription(mainReducer.state);

    const canChooseInvoiceByEmail = (mode == 'edit') && !isDisabled && (mainReducer.state.currentLicense.invoiceByEmail !== undefined);

    // Expandir automáticamente si hay campos vacíos
    useEffect(() => {
        const formValues = billingForm.formValues;
        if (
            (!formValues.address || formValues.address.trim() === '') ||
            (!formValues.name || formValues.name.trim() === '') ||
            (!formValues.province || formValues.province.trim() === '') ||
            (!formValues.town || formValues.town.trim() === '') ||
            (!formValues.zip_code || formValues.zip_code.trim() === '') ||
            (!formValues.vat_number || formValues.vat_number.trim() === '')
        ) {
            setStateExpanded(true);
        }
    }, []);

    // Expandir si hay errores de validación
    useEffect(() => {
        if (billingForm.hasErrors && billingForm.wantsAnInvoice) {
            setStateExpanded(true);
        }
    }, [billingForm.hasErrors, billingForm.wantsAnInvoice]);

    useEffect(() => {
        if (mainReducer.state.countries !== false) {
            setStateLoading(false);
        }
    }, [mainReducer.state.countries]);

    const handleEdit = () => {
        setStateExpanded(!stateExpanded);
    };

    const handleWantsAnInvoice = ({ target }) => {
        billingForm.setWantsAnInvoice(target.checked);
    };

    return (
        <fieldset className="e__plns__fact">
            <legend>
                {getText('facturacion')}
                <span className="e__btn__info">
                    <button className="e__btn" aria-label={getText('mas_informacion')} title={getText('mas_informacion')}>i</button>
                    <span className="e__btn__info__wp">
                        {getText('facturacion_txt')}
                        {(!mainReducer.state.EU) && (
                            <>
                                <br /><br />
                                {getText('no_ue_txt')}
                            </>
                        )}
                    </span>
                </span>
            </legend>
            <div className="e__plns__fact__wpr">
                <div className="e__plns__fact__hdg">
                    <div className="e__plns__fact__hdg__hd">
                        <div className="e__chck">
                            <label htmlFor="cFact">{getText('datos_de_facturacion')}</label>
                            <span className="btn__toggle visuallyhidden">
                                <input
                                    type="checkbox"
                                    id="cFact"
                                    onChange={handleWantsAnInvoice}
                                    checked={billingForm.wantsAnInvoice && !isDisabled}
                                    disabled={isDisabled}
                                />
                                <span className="slider round"></span>
                            </span>
                        </div>
                    </div>
                    <div className="e__txt">
                        {(!mainReducer.state.EU) && (
                            <span className="e__txt__info">{getText('no_ue_txt')}</span>
                        )}
                    </div>
                </div>
                {billingForm.wantsAnInvoice && !isDisabled && (
                    <>
                        {stateExpanded ? (
                            stateLoading ? (
                                <BlockLoading />
                            ) : (
                                <FormProvider {...billingForm.methods}>
                                    <form className="e__form" onSubmit={(e) => e.preventDefault()}>
                                        <SelectForm
                                            id="country"
                                            type="text"
                                            name="country"
                                            defaultValue={billingForm.formValues.country}
                                            options={mainReducer.state.countries}
                                            label={getText('field_billing_country_name')}
                                        />
                                        <InputForm
                                            id="name"
                                            name="name"
                                            type="text"
                                            placeholder={getText('field_billing_name_placeholder')}
                                            label={getText('field_billing_name_name')}
                                        />
                                        <InputForm
                                            id="vat_number"
                                            name="vat_number"
                                            type="text"
                                            maxLength={20}
                                            placeholder={getText('field_billing_vat_number_placeholder')}
                                            label={getText('field_billing_vat_number_name')}
                                        />
                                        <InputForm
                                            id="address"
                                            name="address"
                                            type="text"
                                            placeholder={getText('field_billing_address_placeholder')}
                                            label={getText('field_billing_address_name')}
                                        />
                                        <InputForm
                                            id="town"
                                            name="town"
                                            placeholder={getText('field_billing_town_placeholder')}
                                            label={getText('field_billing_town_name')}
                                            type="text"
                                        />
                                        {mainReducer.state.provinces[billingForm.formValues.country] !== undefined ? (
                                            <SelectForm
                                                id="province"
                                                type="text"
                                                name="province"
                                                placeholder={getText('field_billing_province_placeholder')}
                                                label={getText('field_billing_province_name')}
                                                options={mainReducer.state.provinces[billingForm.formValues.country]}
                                                canBeEmpty={true}
                                            />
                                        ) : (
                                            <InputForm
                                                id="province"
                                                name="province"
                                                type="text"
                                                placeholder={getText('field_billing_province_placeholder')}
                                                label={getText('field_billing_province_name')}
                                            />
                                        )}
                                        <InputForm
                                            id="zip_code"
                                            name="zip_code"
                                            placeholder={getText('field_billing_zip_code_placeholder')}
                                            label={getText('field_billing_zip_code_name')}
                                            type="text"
                                        />
                                    </form>
                                </FormProvider>
                            )
                        ) : (
                            <fieldset className="e__addrs">
                                <BlockBillingAddress />
                                <button
                                    className="e__btn e__btn__trtry e__btn__trtry--mini"
                                    title={getText('cambiar')}
                                    onClick={handleEdit}
                                    disabled={!mainReducer.state.editableBudget}
                                >
                                    <span className='e__btn__ico'><SVG_edit /></span>
                                </button>
                            </fieldset>
                        )}
                    </>
                )}
                {canChooseInvoiceByEmail && <BlockInvoiceByEmail />}
            </div>
        </fieldset>
    );
};
